import style from "./AccountSubscription.module.scss";
import AccountHeader from "./AccountHeader";
import useScreenSize from "../../hooks/useScreenSize";
import { useMemo } from "react";
import { apiService } from "../../api/apiAdapter";
import { formatExpiryDate } from "../../features/userProfile/mySubscriptions";
import { Button } from "../Button/Button";
import { generateToken } from "../../features/userProfile/mySubscriptions";
import { useNavigate } from "react-router-dom";
import { isPWAInstalled } from "../../utils";
import useAuth from "../../hooks/useAuth";

const TG_IDS = ["151", "168"];

function canRenew({ isChild, isWebScreen, tg_subscriptionid, expiryDate }) {
  // Prevent renewal for multi-user subscriptions
  if (tg_subscriptionid === "168") {
    return false;
  }

  // Base eligibility check
  if (isChild || isPWAInstalled()) {
    return false;
  }

  if (!expiryDate) return false;

  try {
    const expiry = new Date(expiryDate);
    const now = new Date();

    // Window: from 3 months before expiry to 1 month after expiry
    const threeMonthsBefore = new Date(expiry);
    threeMonthsBefore.setMonth(expiry.getMonth() - 3);

    const oneMonthAfter = new Date(expiry);
    oneMonthAfter.setMonth(expiry.getMonth() + 1);

    return now >= threeMonthsBefore && now <= oneMonthAfter;
  } catch (err) {
    console.error("Error evaluating renewal window:", err);
    return false;
  }
}

const AccountSubscriptions = ({ showHeader = true }) => {
  const { isWebScreen } = useScreenSize();
  const {
    authDetails,
    userProfile,
    userProfileError: error,
    userProfileLoading: loading,
    refreshUserProfile,
  } = useAuth();
  const profileData = userProfile;
  const contact = profileData?.contact;
  const isChild = !!contact?._tg_parentcontactid_value;
  const navigate = useNavigate();

  const subscriptions = useMemo(() => {
    const all = profileData?.subscriptions ?? [];
    return all.find((sub) => TG_IDS.includes(String(sub.tg_subscriptionid))) ?? {};
  }, [profileData]);
  const showRenewButton = useMemo(() => {
    if (!subscriptions?.tg_subscriptionid) return false;
    const renewable = canRenew({
      isChild,
      isWebScreen,
      tg_subscriptionid: subscriptions.tg_subscriptionid,
      expiryDate: subscriptions.tg_expirydate,
    });
    return renewable;
  }, [subscriptions, isChild, isWebScreen]);

  const handleRetry = async () => {
    if (authDetails?.type !== "user") {
      return;
    }

    try {
      await refreshUserProfile();
    } catch (retryError) {
      console.error("Profile fetch error:", retryError);
    }
  };
  const handleRenewalClick = async () => {
    const existingCartItem = localStorage.getItem("tgCartToken");
    if (existingCartItem) {
      localStorage.removeItem("tgCartToken");
    }
    const token = generateToken();
    localStorage.setItem("tgCartToken", token);

    const renewalItem = {
      token,
      subscriptionid: subscriptions.tg_subscriptionid.toString(),
      quantity: subscriptions.tg_numberoflicences || 1,
      ordertype: "Renew",
      currentSubscriptionId: subscriptions.entitlementid,
      enddate: subscriptions.tg_expirydate,
      hasappliedcoupon: subscriptions.tg_couponreferrercode !== null,
    };

    try {
      const response = await apiService.addToCart(renewalItem);
      if (response.success) {
        setTimeout(() => navigate("/myCart"), 2000);
      } else {
        throw new Error(response.message || "Failed to add renewal to cart");
      }
    } catch (error) {
      console.error("Failed to add renewal to cart:", error);
      alert(error.message || "An error occurred while adding the renewal to cart.");
    }
  };

  const isRenewalPeriod = () => {
    if (!subscriptions?.tg_expirydate) return false;
    if (subscriptions.tg_numberoflicences > 5) return false;
    const endDate = new Date(subscriptions.tg_expirydate);
    const today = new Date();

    // Calculate dates 4 months before and after expiry
    const fourMonthsBefore = new Date(endDate);
    fourMonthsBefore.setMonth(endDate.getMonth() - 3);

    const threeMonthsAfter = new Date(endDate);
    threeMonthsAfter.setMonth(endDate.getMonth() + 1);
    return today >= fourMonthsBefore && today <= threeMonthsAfter;
  };

  if (loading) {
    return (
      <div className={style.accountContainer}>
        <AccountHeader headername="Subscriptions" />
        <div className={style.loaderContainer}>
          <div className={style.loader}></div>
          <p>Loading subscription data...</p>
        </div>
      </div>
    );
  }

  if (authDetails?.type !== "user") {
    return (
      <div className={style.accountContainer}>
        {showHeader && <AccountHeader headername="Subscriptions" />}
      </div>
    );
  }

  return (
    <div className={style.accountContainer}>
      {showHeader && <AccountHeader headername="Subscriptions" />}
      <div className={style.subscriptionContainer}>
        <p className={style.title}>Subscription summary</p>

        {error ? (
          <div className={style.errorContainer}>
            <p className={style.errorMessage}>Sorry, we can't find your subscription details!</p>
            <Button onClick={handleRetry} bgColor="black">
              Retry
            </Button>
          </div>
        ) : (
          <div className={style.subscriptionCard}>
            <div className={style.subscriptionRow}>
              <span className={style.subscriptionLabel}>Products:</span>
              <span className={style.subscriptionValue}>
                Therapeutic Guidelines individual subscription
              </span>
            </div>

            {subscriptions?.tg_expirydate && (
              <div className={style.subscriptionRow}>
                <span className={style.subscriptionLabel}>Expiry:</span>
                <span className={style.subscriptionValue}>
                  {formatExpiryDate(subscriptions.tg_expirydate)}
                </span>
              </div>
            )}

            <p className={style.subscriptionNote}>
              If you want to purchase/renew an account with more than 1 licence, please contact our
              Sales Team at{" "}
              <a href="mailto:sales@tg.org.au" className={style.emailLink}>
                sales@tg.org.au
              </a>{" "}
              or call us on 1800 061 260
            </p>
            {showRenewButton && (
              <button
                type="button"
                className={style.renewButton}
                disabled={!isRenewalPeriod()}
                onClick={handleRenewalClick}
              >
                Renew Subscription
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AccountSubscriptions;
